/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useState } from 'react';
import { ActivityIndicator, Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing, ticketColors, typography } from '../../theme/public-api';
import { productSearchEmptyMessage } from '../productSearchUx';

export type ProductSearchSuggestionItem = {
  id: string;
  title: string;
  subtitle?: string | null;
  /** Thumb opt-in (mismo contrato que TicketListRow.leadingImage). */
  imageUrl?: string | null;
};

/** Thumb 34px con fallback a ícono neutro cuando no hay imagen o falla la carga. */
const SuggestionThumb = ({ imageUrl }: { imageUrl?: string | null }) => {
  const [failed, setFailed] = useState(false);
  const show = Boolean(imageUrl) && !failed;
  return (
    <View style={styles.thumb}>
      {show ? (
        <Image
          source={{ uri: imageUrl! }}
          style={styles.thumbImage}
          resizeMode="contain"
          onError={() => setFailed(true)}
        />
      ) : (
        <Ionicons name="cube-outline" size={16} color={ticketColors.ink3} />
      )}
    </View>
  );
};

type Props = {
  open: boolean;
  loading?: boolean;
  items: ProductSearchSuggestionItem[];
  selectedId?: string | null;
  /** null = no mostrar mensaje vacío (el caller ya renderiza un empty con acciones). */
  emptyMessage?: string | null;
  onPick: (item: ProductSearchSuggestionItem) => void;
  loadMoreLabel?: string;
  onLoadMore?: () => void;
  loadingMore?: boolean;
};

/**
 * Panel de sugerencias bajo el buscador unificado (catálogo, stock, modales).
 */
export function ProductSearchSuggestionsPanel({
  open,
  loading = false,
  items,
  selectedId = null,
  emptyMessage = productSearchEmptyMessage(true),
  onPick,
  loadMoreLabel,
  onLoadMore,
  loadingMore = false,
}: Props) {
  if (!open) return null;
  // Sin mensaje vacío configurado, el panel no muestra un hint "sin resultados".
  if (!loading && items.length === 0 && emptyMessage == null) return null;

  return (
    <View style={styles.footerBox}>
      {loading ? (
        <View style={styles.loadingPad}>
          <ActivityIndicator color={ticketColors.ink2} accessibilityLabel="Buscando productos" />
        </View>
      ) : items.length === 0 ? (
        <Text style={styles.emptyHint}>{emptyMessage}</Text>
      ) : (
        <ScrollView keyboardShouldPersistTaps="handled" style={styles.scroll} nestedScrollEnabled>
          {items.map((item) => {
            const selected = selectedId === item.id;
            return (
              <TouchableOpacity
                key={item.id}
                style={[styles.row, selected && styles.rowSelected]}
                onPress={() => onPick(item)}
                accessibilityRole="button"
                accessibilityLabel={`Seleccionar ${item.title}`}
                accessibilityState={{ selected }}
              >
                {item.imageUrl !== undefined ? <SuggestionThumb imageUrl={item.imageUrl} /> : null}
                <View style={styles.rowInner}>
                  <Text style={styles.rowTitle} numberOfLines={2}>
                    {item.title}
                  </Text>
                  {item.subtitle ? (
                    <Text style={styles.rowSku} numberOfLines={2}>
                      {item.subtitle}
                    </Text>
                  ) : null}
                </View>
              </TouchableOpacity>
            );
          })}
          {loadingMore ? (
            <View style={styles.loadingPad}>
              <ActivityIndicator color={ticketColors.ink2} size="small" />
            </View>
          ) : null}
          {onLoadMore && loadMoreLabel && !loadingMore ? (
            <TouchableOpacity style={styles.loadMoreBtn} onPress={onLoadMore} accessibilityRole="button">
              <Text style={styles.loadMoreText}>{loadMoreLabel}</Text>
            </TouchableOpacity>
          ) : null}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  footerBox: {
    marginTop: spacing.xs,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    maxHeight: 220,
    backgroundColor: ticketColors.paper,
  },
  loadingPad: {
    padding: spacing.md,
    alignItems: 'center',
  },
  emptyHint: {
    ...typography.caption,
    padding: spacing.md,
    color: ticketColors.ink3,
  },
  scroll: {
    maxHeight: 216,
  },
  row: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  rowSelected: {
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
  },
  thumb: {
    width: 34,
    height: 34,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  thumbImage: {
    width: '100%',
    height: '100%',
  },
  rowInner: {
    gap: 4,
    minWidth: 0,
    flex: 1,
  },
  rowTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  rowSku: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  loadMoreBtn: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  loadMoreText: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
  },
});
